// File: src/pages/sheearns/SheEarnsFAQ.jsx
import React, { useState } from "react";

const faqData = {
  "Women Entrepreneur": [
    // Negative
    {
      sentiment: "negative",
      question: "I don’t have a business yet — is this platform even for me?",
      answer:
        "Absolutely! SheEarns helps you start from wherever you are — whether you want to sell products, offer services, or learn a skill first. You can grow step-by-step at your own pace.",
    },
    {
      sentiment: "negative",
      question: "I’m not very tech-savvy. Will I be able to use this easily?",
      answer:
        "Yes! The platform is made mobile-first, with voice guidance, icons, and local language support. Many women with no tech background use SheEarns confidently.",
    },
    {
      sentiment: "negative",
      question: "Why should I trust another online platform?",
      answer:
        "SheEarns is built by Think5Whys Foundation with verified mentors, trainers, and partners. Your data stays private, and your progress is tracked transparently.",
    },
    {
      sentiment: "negative",
      question: "Can I actually earn from home through this?",
      answer:
        "Yes. Thousands of women already earn by selling handmade products, teaching, reselling, or providing digital services via SheEarns and SAKSHAM Shakti programs.",
    },
    {
      sentiment: "negative",
      question: "I don’t have money to start a business — will this still help?",
      answer:
        "Definitely. The platform connects you to micro-grants, zero-cost tools, and women’s collectives that support each other through shared opportunities.",
    },
    // Neutral
    {
      sentiment: "neutral",
      question: "What kind of support does SheEarns provide?",
      answer:
        "You’ll get access to digital skill training, mentorship circles, financial literacy, and business listing tools — all designed for women entrepreneurs.",
    },
    {
      sentiment: "neutral",
      question: "Is there a mobile app available?",
      answer:
        "Yes. The SheEarns mobile app is lightweight and optimized for low-data, low-storage phones, so it works even in rural areas.",
    },
    {
      sentiment: "neutral",
      question: "Can I connect with other women like me?",
      answer:
        "Yes! Join communities in your category — from tailoring to teaching — to share experiences, tips, and collaborations.",
    },
    // Positive
    {
      sentiment: "positive",
      question: "How will SheEarns help me grow financially?",
      answer:
        "You’ll learn how to price your work, save wisely, and market online. The platform connects you to buyers, mentors, and local partners who help increase income.",
    },
    {
      sentiment: "positive",
      question: "Can I promote my local products or services?",
      answer:
        "Yes! You can create your SheEarns digital profile, add photos, and share links across social media or local groups to attract customers.",
    },
    {
      sentiment: "positive",
      question: "Will I get certification for training or workshops?",
      answer:
        "Yes. Upon completion of SheEarns and SAKSHAM Shakti workshops, you receive digital certificates recognized by our partner institutions.",
    },
  ],

  "Mentor / Trainer": [
    {
      sentiment: "negative",
      question: "I’m already busy with my classes — why join SheEarns?",
      answer:
        "Because your expertise can reach hundreds of women through digital sessions. The platform handles logistics, so you focus only on teaching or mentoring.",
    },
    {
      sentiment: "negative",
      question: "How are trainers compensated or recognized?",
      answer:
        "You can earn through workshops, receive digital recognition, and grow your reach as an official SheEarns Mentor.",
    },
    {
      sentiment: "neutral",
      question: "Can I host both free and paid workshops?",
      answer:
        "Yes, you can design sessions as free, sponsored, or ticketed events — with flexible scheduling and easy enrollment tools.",
    },
    {
      sentiment: "neutral",
      question: "Is there support for creating training content?",
      answer:
        "Our team provides templates, recording tools, and best practices to help you deliver high-quality online or community sessions.",
    },
    {
      sentiment: "positive",
      question: "Can I mentor women outside my city?",
      answer:
        "Definitely! The platform connects you with learners across states using multilingual chat, video calls, and digital classrooms.",
    },
  ],

  "Community Partner": [
    {
      sentiment: "negative",
      question: "Our NGO already runs training — why use SheEarns?",
      answer:
        "SheEarns strengthens your impact with digital tools for tracking progress, issuing certificates, and showcasing local success stories globally.",
    },
    {
      sentiment: "neutral",
      question: "Can we onboard our own women members?",
      answer:
        "Yes, you can invite members, monitor learning, and co-brand programs under the SheEarns & SAKSHAM Shakti umbrella.",
    },
    {
      sentiment: "neutral",
      question: "Do we get visibility for our initiatives?",
      answer:
        "Partner NGOs and SHGs are featured in SheEarns showcases, highlighting your grassroots impact and leadership.",
    },
    {
      sentiment: "positive",
      question: "Can this help us access funding or collaborations?",
      answer:
        "Yes! Verified partners become eligible for grants, CSR tie-ups, and institutional collaborations facilitated by Think5Whys Foundation.",
    },
  ],

  "Student / Learner": [
    {
      sentiment: "negative",
      question: "I’m still in school or college — how can this help me?",
      answer:
        "You can start learning essential digital and life skills early — from freelancing to financial literacy — preparing you for independence.",
    },
    {
      sentiment: "neutral",
      question: "Is SheEarns only for women in business?",
      answer:
        "Not at all. It’s for any girl or woman who wants to learn skills, grow confidence, and explore income opportunities.",
    },
    {
      sentiment: "positive",
      question: "Can I get a mentor to guide me?",
      answer:
        "Yes. Each registered learner can choose a mentor and participate in mentorship circles for guidance, projects, and real-world exposure.",
    },
  ],

  "Policy / Government": [
    {
      sentiment: "negative",
      question: "Will women in rural areas actually use this platform?",
      answer:
        "Yes. The platform is voice-enabled, supports regional languages, and runs even on low internet speed — ensuring rural inclusion.",
    },
    {
      sentiment: "neutral",
      question: "Can SheEarns data help in gender-based policy design?",
      answer:
        "Aggregated data highlights trends in women’s entrepreneurship, digital literacy, and income generation for evidence-based policymaking.",
    },
    {
      sentiment: "positive",
      question: "Can this initiative align with national skilling missions?",
      answer:
        "SheEarns integrates with existing government missions like Skill India, Digital India, and PM EGP to scale women’s participation.",
    },
  ],
};

// Sentiment color mapping
const sentimentColors = {
  negative: "text-pink-400",
  neutral: "text-gray-400",
  positive: "text-green-400",
};

export default function SheEarnsFAQ() {
  const stakeholders = Object.keys(faqData);
  const [selectedStakeholder, setSelectedStakeholder] = useState(stakeholders[0]);
  const [expandedIndex, setExpandedIndex] = useState(null);

  return (
    <div className="min-h-screen bg-gray-950 text-white p-4 sm:p-8">
      <h1 className="text-lg sm:text-xl font-bold mb-6 text-center text-pink-400">
        SheEarns & SAKSHAM Shakti – FAQ
      </h1>

      {/* Stakeholder Tabs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 mb-6">
        {stakeholders.map((stakeholder) => (
          <button
            key={stakeholder}
            onClick={() => {
              setSelectedStakeholder(stakeholder);
              setExpandedIndex(null);
            }}
            className={`px-4 py-2 rounded-md font-semibold text-sm sm:text-base transition-all duration-200 ${
              selectedStakeholder === stakeholder
                ? "bg-pink-500 text-white"
                : "bg-gray-800 hover:bg-gray-700"
            }`}
          >
            {stakeholder}
          </button>
        ))}
      </div>

      {/* FAQ List */}
      <div className="max-w-4xl mx-auto">
        {faqData[selectedStakeholder].map((faq, index) => (
          <div
            key={index}
            className="border-b border-gray-700 py-3 cursor-pointer"
            onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
          >
            <div className="flex justify-between items-center">
              <h3 className={`font-medium ${sentimentColors[faq.sentiment]}`}>
                {faq.question}
              </h3>
              <span className="text-gray-400 text-xl">
                {expandedIndex === index ? "-" : "+"}
              </span>
            </div>
            {expandedIndex === index && (
              <p className="mt-2 text-gray-300">{faq.answer}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
